import { Grid, Checkbox, Chip } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column, TableInstance } from "react-table";
import { MainCard } from "../../../components/surface/MainCard";
import { IMotor } from "../../../models/Configuration";
import { formatDate } from "../../../helpers/utils/formatDate";
import { Table } from "../../../components/table/Table";
import { SelectColumnFilter } from "../../../components/table/filters/SelectColumnFilter";
import { convertValue } from "../../../helpers/utils/valueConverter";
import { apiStore } from "../../../api/ApiFactory";

const Motor = () => {
    const navigate = useNavigate();
    const [isLocked, setIsLocked] = useState<boolean>(true);

  const handleToggle = useCallback(
      (instance: TableInstance<IMotor>) => () => {
          setIsLocked(prevState => !prevState);
      },
      []
  );

  const columns: Column<IMotor>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Part Num",
        accessor: "partNumber",
      },
      {
        Header: "Price",
        accessor: "price",
        width: 120,
      },
      {
        Header: "Is Active",
        accessor: "isActive",
        InLineEdit: true,
        inputType: 'checkbox',
        width: 120,
        Cell: (cell: any) => (
          <Checkbox
            className="row-checkbox"
            disabled
            checked={cell.value ? true : false}
          />
        ),
      },
      {
        Header: "Is Loose Motor Active",
        accessor: "isLooseMotorActive",
        InLineEdit: true,
        inputType: 'checkbox',
        width: 140,
        Cell: (cell: any) => (
          <Checkbox
            className="row-checkbox"
            disabled
            checked={cell.value ? true : false}
          />
        ),
      },
      {
        Header: "Created Date",
        accessor: "createdDate",
        Cell: (cell: any) => formatDate(cell.value),
      },
      {
        Header: "Horsepower",
        accessor: "horsepower",
      },
      {
        Header: "Series",
        accessor: "seriesMotors",
        Filter: SelectColumnFilter,
        filter: "inlist",
        Cell: (cell: any) => <div>{showSeriesMotors(cell.value)}</div>,
        width: 200,
        maxWidth: 200,
      },
      {
        Header: "Pre-Rig",
        accessor: "preRigs",
        Filter: SelectColumnFilter,
        filter: "inlist",
        Cell: (cell: any) => <div>{showPrerig(cell.value)}</div>,
        width: 300,
        maxWidth: 300,
      },
    ],
    []
  );

  const onAdd = useCallback(
    (instance: TableInstance<IMotor>) => () => {
      navigate("/config/motor/add", { state: { data: null } });
    },
    []
  );

  const onEdit = useCallback(
    (instance: TableInstance<IMotor>) => () => {
      const data = instance.selectedFlatRows[0].original;
      navigate("/config/motor/edit", { state: { data } });
    },
    []
  );

  const showPrerig = (value: any) => (
    <Grid container spacing={1} direction="row">
      {value.map((prerig: { name: string; value: string }, index: number) => (
        <Grid item key={index}>
          <Chip label={prerig.name} />
        </Grid>
      ))}
    </Grid>
    );

    const updateMotor = async (values: IMotor): Promise<boolean> => {
        try {
            await apiStore.boatConfigApi.saveMotor(values);
            console.log('Update successful');
            return true;
        } catch (error) {
            console.error('Update failed:', error);
            return false;
        }
    };

    const onInlineEdit = useCallback(
        async (rowId: string | number, columnId: keyof IMotor, newValue: any, row: IMotor) => {
            if (row) {
                const oldValue = row[columnId];
                const newValueConverted = convertValue(oldValue, newValue);

                // Only update if the new value is different from the old value
                if (oldValue !== newValueConverted) {
                    const updatedValues: IMotor = {
                        ...row,
                        [columnId]: newValueConverted,
                    };

                    const result = await updateMotor(updatedValues);
                    if (result) {
                        return true;
                    }
                } else {
                    console.log('No change detected. Update skipped.');
                }
            } else {
                console.error('Row not found:', rowId);
            }
            return false;
        },
        []
    );

  const showSeriesMotors = (value: any) => (
    <Grid container spacing={1} direction="row">
      {value.map(
        (seriesMotors: { id: number; name: string }, index: number) => (
          <Grid item key={index}>
            <Chip label={seriesMotors.name} />
          </Grid>
        )
      )}
    </Grid>
  );

  return (
    <Grid container>
      <Grid item xs={12}>
        <MainCard title="Motor">
          <Table<IMotor>
            name="motor-list"
            serverOnChange={false}
            columns={columns}
            apiGet="/parts"
            onEdit={onEdit}
            onAdd={onAdd}
            inlineLock={handleToggle}
            inlineLockState={isLocked}
            onInlineEdit={onInlineEdit}
            initialFilters={[
              {
                id: "isActive",
                value: "true",
              },
            ]}
          />
        </MainCard>
      </Grid>
    </Grid>
  );
};

export default Motor;
